import { useTranslation } from "react-i18next";
import { Grid, GridColumn } from "semantic-ui-react";

import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { formatPrice } from "utils/formatPrice";

import type { Appointment } from "models";
import type { Totals } from "utils/common";

type TotalPriceBarProps = {
  showPriceInVat: boolean;
  appointment: Appointment | null;
  interventionTotals: Totals;
};

export const TotalPriceBar = ({ appointment, interventionTotals, showPriceInVat }: TotalPriceBarProps) => {
  const { t } = useTranslation();
  const appointmentLocation = useAppointmentLocation();

  if (!appointment) return null;

  const hasInterventions = appointment.interventions && appointment.interventions.length > 0;
  const isDmsPriceEnabled = appointmentLocation?.dms_price_enabled;

  if (!hasInterventions || !isDmsPriceEnabled) return null;

  const hasMultipleRates = interventionTotals.vatGroups.length > 1;
  const vatGroups = interventionTotals.vatGroups.filter(group => group.rate > 0 && group.amount > 0);

  return (
    <Grid className="total-amount-container">
      <GridColumn width={6}>
        {showPriceInVat &&
          hasMultipleRates &&
          vatGroups.map(group => (
            <p key={group.rate}>
              {t("v8_vat", "VAT")} {group.rate}%: <strong>€ {formatPrice(group.amount)}</strong>
            </p>
          ))}
      </GridColumn>
      <GridColumn width={4}>
        <p>
          {`${t("v8_total_amount", "Total amount")}${showPriceInVat ? `, ${t("v8_including_VAT", "including VAT")}:` : `, ${t("v8_excluding_VAT", "excluding VAT")}:`}`}{" "}
        </p>
      </GridColumn>
      <GridColumn width={2}>
        <p className="no-float">
          <strong>€ {formatPrice(interventionTotals.totalAmount)}</strong>
        </p>
      </GridColumn>
      <GridColumn width={4}>
        <p>
          {`${t("v8_approved_total", "Approved total")}${showPriceInVat ? `, ${t("v8_including_VAT", "including VAT")}:` : `, ${t("v8_excluding_VAT", "excluding VAT")}:`}`}{" "}
          <strong>€ {formatPrice(interventionTotals.approvedTotal)}</strong>
        </p>
      </GridColumn>
    </Grid>
  );
};
